import {
  QueryFactory,
  isAxiosError,
  useEnabledDataBlockTypes,
} from '@lmt/shared-types';
import { EditorEmptyBlockIcon } from '@lmt/unicorn-shared/app/components/common/icons/ui-editor/EditorEmptyBlockIcon';
import React, { useMemo, useRef, useState } from 'react';
import RGL from 'react-grid-layout';
import { IconButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/IconButton';
import { UniLink } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/UniLink';
import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { parseError } from '@lmt/unicorn-shared/app/helpers/error-handling';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { Loading } from '../common/Loading';
import { Input } from '../common/ui-kit/inputs/Input';
import { CheckBox } from '../common/ui-kit/CheckBox';
import { DataBlockTypeDto, CreateUIConfigurationDto } from '@lmt/shared-types';

import styles from './UIConfigurator.module.scss';
import SmallCloseIcon from '@lmt/unicorn-shared/app/icons/gray-cross.svg?react';
import CloseIcon from '@lmt/unicorn-shared/app/icons/cross_20.svg?react';
import ResetIcon from '@lmt/unicorn-shared/app/icons/reset_20.svg?react';
import SaveIcon from '@lmt/unicorn-shared/app/icons/check_20.svg?react';
import DeleteIcon from '@lmt/unicorn-shared/app/icons/trash_20.svg?react';
import PointsIcon from '@lmt/unicorn-shared/app/icons/ui-editor/points-vertical.svg?react';
import { UIConfigurationsActions } from '@lmt/unicorn-shared/app/features/ui-configurations/reducer';
import { snakeCase } from 'lodash';
import { errorBoundaryFallbackFunction } from '../../helpers/ErrorBoundary';
import { ErrorBoundary } from '@sentry/react';
import { useEventCallback } from '@mui/material';
import { createId } from '../../helpers/create-id';

const ReactGridLayout = RGL;

export interface UIConfigBlock {
  id: string;
  component?: JSX.Element;
  static?: boolean;
  minW?: number;
  maxW?: number;
  minH?: number;
  maxH?: number;
  w: number;
  h: number;
  isHidden?: boolean;
}

const defaultDashboardBlocksNames = [
  'plannedVisits',
  'nightView',
  'incompleteVisits',
  'recentVisits',
  'notes',
  'currentVisits',
];

const defaultPatientBlocksNames = [
  'patientInfo',
  'diagnoses',
  'comments',
  'visits',
  'documents',
];

export enum UIConfigurationType {
  Dashboard = 1,
  PatientOverview = 2,
}

interface UIConfiguratorProps {
  blocks: { [key: string]: UIConfigBlock };
  isEditMode: boolean;
  configType: UIConfigurationType;
  closeEditMode: (value: boolean) => void;
  title: string;
  configId?: number;
}

// when we need to add one more UIConfigurationType
// we should do it like in dictionaries.types.ts,
// and not add it to conditions below
export const UIConfigurator: React.FC<UIConfiguratorProps> = (props) => {
  return (
    <ErrorBoundary fallback={errorBoundaryFallbackFunction}>
      <UIConfiguratorInner {...props} />
    </ErrorBoundary>
  );
};
const UIConfiguratorInner: React.FC<UIConfiguratorProps> = (props) => {
  const { blocks, isEditMode, configType, closeEditMode, title, configId } =
    props;
  const dispatch = useUniDispatch();
  const modals = useModal();
  const queryClient = useQueryClient();
  const i18next = useScopedTranslation('ui_configurator');
  const claims = useClaims();
  const [layout, setLayout] = useState<RGL.Layout[]>([]);
  const [currentDropElement, setCurrentDropElement] = useState<string>();
  const [rowHeight, setRowHeight] = useState<number>(200);
  const [gridWidth, setGridWidth] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [configName, setConfigName] = useState<string>('');
  const [isShared, setShared] = useState<boolean>(false);
  const dataBlockTypes = useEnabledDataBlockTypes();
  const dataBlockTypesDict = useMemo(() => {
    const dict: { [key: string]: DataBlockTypeDto } = {};
    dataBlockTypes.forEach((dataBlockType) => {
      dict[dataBlockType.id.toString()] = dataBlockType;
    });
    return dict;
  }, [dataBlockTypes]);

  const blocksContainer = useRef<HTMLDivElement>(null);

  const configurationQuery =
    QueryFactory.UIConfigurationQuery.useGetConfigurationQuery(configId!, {
      enabled: !!configId,
      // if we don't set throwOnError to false, we get an error screen if UIConfiguration was removed
      // (we receive 404, which is handled correctly in code)
      //
      throwOnError: false,
    });
  if (configurationQuery.error) {
    // do not throw for 404, since it's handled correctly in code
    if (
      !isAxiosError(configurationQuery.error) ||
      configurationQuery.error.status !== 404
    )
      throw configurationQuery.error;
  }

  const isEditSharedUIConfiguration =
    configType === UIConfigurationType.Dashboard
      ? claims.permissions.editTenantDashboardUIConfiguration
      : configType === UIConfigurationType.PatientOverview
        ? claims.permissions.editTenantPatientOverviewUIConfiguration
        : false;

  const defaultBlockNames: string[] =
    configType === UIConfigurationType.Dashboard
      ? defaultDashboardBlocksNames
      : configType === UIConfigurationType.PatientOverview
        ? defaultPatientBlocksNames
        : [];

  useEffect(() => {
    if (!configId) {
      setLayout([]);
      setShared(false);
      setConfigName('');
    }
  }, [configId]);

  useEffect(() => {
    if (configurationQuery.data) {
      const defaultLayout: RGL.Layout[] = JSON.parse(
        configurationQuery.data.uiConfig,
      );
      setLayout(defaultLayout);
      setShared(configurationQuery.data.isShared);
      setConfigName(configurationQuery.data.name);
    }
  }, [configurationQuery.data]);
  const windowWidth = window.innerWidth;
  useEffect(
    function onWindowResize() {
      handleResize();
    },
    [windowWidth],
  );

  useEffect(() => {
    if (
      configurationQuery.error &&
      isAxiosError(configurationQuery.error) &&
      configurationQuery.error.status === 404
    ) {
      if (configType === UIConfigurationType.PatientOverview) {
        dispatch(
          UIConfigurationsActions.patientOverviewUIConfigurationIdChanged(null),
        );
      }
    }
  }, [configurationQuery.error, configType, dispatch]);

  const handleResize = useEventCallback(() => {
    if (blocksContainer && blocksContainer.current) {
      const calculatedHeight =
        (blocksContainer.current.clientHeight - 13 * 10) / 12;
      const width = blocksContainer.current.clientWidth;

      if (width > 100) {
        setRowHeight(calculatedHeight);
        setGridWidth(blocksContainer.current.clientWidth);
      }
    }
  });
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  function onDropBlock(
    items: RGL.Layout[],
    layoutItem: RGL.Layout,
    _event: Event,
  ) {
    const layoutProps = blocks[layoutItem.i];
    const newLayout: RGL.Layout = {
      ...layoutItem,
      minH: layoutProps.minH,
      minW: layoutProps.minW,
      maxW: layoutProps.maxW,
      maxH: layoutProps.maxH,
      isDraggable: undefined,
    };
    setLayout(
      items.map((item) => (item.i === layoutItem.i ? newLayout : item)),
    );
  }

  const onRemoveBlock = useEventCallback((a: string) => {
    setLayout(layout.filter((item) => item.i != a));
  });

  const defaultProps: RGL.ReactGridLayoutProps & RGL.WidthProviderProps = {
    className: styles.layout,
    onLayoutChange: setLayout,
    cols: 12,
    isDraggable: isEditMode,
    isResizable: isEditMode,
    resizeHandles: isEditMode ? ['se'] : [],
    isDroppable: isEditMode,
    onDrop: onDropBlock,
    rowHeight: rowHeight,
    onResizeStop: handleResize,
    autoSize: false,
    width: gridWidth,
    useCSSTransforms: false,
  };

  const addingKeys = Object.keys(blocks).filter(
    (key) => !layout.map((item) => item.i).includes(key),
  );

  const invalidateQueries = useEventCallback(async () => {
    if (configType === UIConfigurationType.Dashboard) {
      await queryClient.refetchQueries({
        queryKey:
          QueryFactory.UIConfigurationQuery.getDashboardConfigurationsQueryKey(),
      });
    }
    if (configType === UIConfigurationType.PatientOverview) {
      await queryClient.refetchQueries({
        queryKey:
          QueryFactory.UIConfigurationQuery.getPatientOverviewConfigurationsQueryKey(),
      });
    }
    await queryClient.refetchQueries({
      queryKey:
        QueryFactory.DataBlockTypeQuery.getDataBlockTypesForEditingQueryKey(),
    });
  });

  const onCloseEditMode = useEventCallback(async (cancelChanges?: boolean) => {
    await invalidateQueries();
    if (cancelChanges && configurationQuery.data) {
      const initialLayout: RGL.Layout[] = JSON.parse(
        configurationQuery.data.uiConfig,
      );
      setLayout(initialLayout);
    }
    closeEditMode(false);
  });

  const onSave = useEventCallback(async () => {
    setLoading(true);
    const types: number[] = [];
    for (const item of layout) {
      if (!defaultBlockNames.includes(item.i) && dataBlockTypesDict[item.i]) {
        types.push(dataBlockTypesDict[item.i].id);
      }
    }
    try {
      const dto: CreateUIConfigurationDto = {
        isShared: isShared,
        name: configName,
        uiConfig: JSON.stringify(layout),
        dataBlockTypes: types,
      };
      let createdConfigId: number | null = null;
      if (configId) {
        if (configType === UIConfigurationType.Dashboard) {
          await QueryFactory.UIConfigurationClient.editDashboardConfiguration(
            configId,
            dto,
          );
          dispatch(
            UIConfigurationsActions.dashboardUIConfigurationIdChanged(configId),
          );
        }
        if (configType === UIConfigurationType.PatientOverview) {
          await QueryFactory.UIConfigurationClient.editPatientOverviewConfiguration(
            configId,
            dto,
          );
          dispatch(
            UIConfigurationsActions.patientOverviewUIConfigurationIdChanged(
              configId,
            ),
          );
        }
      } else {
        if (configType === UIConfigurationType.Dashboard) {
          createdConfigId =
            await QueryFactory.UIConfigurationClient.createDashboardConfiguration(
              dto,
            );
          dispatch(
            UIConfigurationsActions.dashboardUIConfigurationIdChanged(
              createdConfigId,
            ),
          );
        }
        if (configType === UIConfigurationType.PatientOverview) {
          createdConfigId =
            await QueryFactory.UIConfigurationClient.createPatientOverviewConfiguration(
              dto,
            );
          dispatch(
            UIConfigurationsActions.patientOverviewUIConfigurationIdChanged(
              createdConfigId,
            ),
          );
        }
      }
      await onCloseEditMode();
      if (configType === UIConfigurationType.Dashboard) {
        dispatch(
          UIConfigurationsActions.dashboardUIConfigurationIdChanged(
            configId ?? createdConfigId,
          ),
        );
      }
      if (configType === UIConfigurationType.PatientOverview) {
        dispatch(
          UIConfigurationsActions.patientOverviewUIConfigurationIdChanged(
            configId ?? createdConfigId,
          ),
        );
      }
    } catch (error) {
      await modals.showError({ text: parseError(error) });
    } finally {
      setLoading(false);
    }
  });

  const onUpdateDefault = useEventCallback(async () => {
    setLoading(true);
    try {
      if (configType === UIConfigurationType.Dashboard) {
        await QueryFactory.UIConfigurationClient.resetDefaultDashboardConfiguration();
      }
      if (configType === UIConfigurationType.PatientOverview) {
        await QueryFactory.UIConfigurationClient.resetDefaultPatientOverviewConfiguration();
      }
      await onCloseEditMode();
    } catch (error) {
      await modals.showError({ text: parseError(error) });
    } finally {
      setLoading(false);
    }
  });

  const onDelete = useEventCallback(async () => {
    if (!configId) {
      return;
    }
    const configName =
      configType === UIConfigurationType.Dashboard
        ? 'dashboard'
        : configType === UIConfigurationType.PatientOverview
          ? 'patient_overview'
          : configType;
    const isConfirmed = await modals.showDeletionConfirmation({
      title: i18next.t(configName + '.delete_confirm_title', {
        defaultValue: configName,
      }),
      text: i18next.t(configName + '.delete_confirm_text', {
        defaultValue: configName,
      }),
      okButtonText: i18next.t(configName + '.delete_confirm_ok_text', {
        defaultValue: configName,
      }),
    });
    if (isConfirmed) {
      setLoading(true);
      try {
        if (configType === UIConfigurationType.Dashboard) {
          await QueryFactory.UIConfigurationClient.deleteDashboardConfiguration(
            configId,
          );
        }
        if (configType === UIConfigurationType.PatientOverview) {
          await QueryFactory.UIConfigurationClient.deletePatientOverviewConfiguration(
            configId,
          );
        }
        await onCloseEditMode();
      } catch (error) {
        await modals.showError({ text: parseError(error) });
      } finally {
        setLoading(false);
      }
    }
  });

  useEffect(() => {
    handleResize();
  }, [configurationQuery.isLoading, loading, isEditMode]);

  // remove scrollbar padding
  useEffect(() => {
    setTimeout(() => {
      handleResize();
    }, 200);
  }, [isEditMode]);

  return (
    <div className={styles.container}>
      <Loading
        loading={(configId && configurationQuery.isLoading) || loading}
        title={i18next.t('loading')}
      >
        <div className={styles.topbar} data-visible={isEditMode}>
          <div className={styles.controlsContainer}>
            <div className={styles.topbarTitle}>{title}</div>
            <div className={styles.controlContainer}>
              <Input
                className={styles.input}
                disabled={configurationQuery.data?.isDefault}
                value={configName}
                onChange={(e) => setConfigName(e.currentTarget.value)}
                testId="patient-page-config-input"
              />
            </div>
            {isEditSharedUIConfiguration && (
              <div
                className={styles.controlContainer}
                data-test-id="share-to-other-users"
              >
                <CheckBox
                  disabled={configurationQuery.data?.isDefault}
                  name={`isShared_${createId()}`}
                  title={i18next.t('is_shared')}
                  checked={isShared}
                  onChange={() => setShared(!isShared)}
                />
              </div>
            )}
          </div>

          <div className={styles.controlsContainer}>
            {configurationQuery.data && !configurationQuery.data?.isDefault && (
              <UniLink
                color={ButtonColor.Danger}
                icon={<DeleteIcon />}
                title={i18next.t('delete_button')}
                onClick={onDelete}
                className={styles.control}
                testId="patient-page-delete-button"
              />
            )}
            {configurationQuery.data?.isDefault &&
              configurationQuery.data?.isUpdated && (
                <UniLink
                  color={ButtonColor.Primary}
                  icon={<ResetIcon />}
                  title={i18next.t('reset_button')}
                  onClick={onUpdateDefault}
                  className={styles.control}
                  testId="patient-page-reset-button"
                />
              )}
            <UniLink
              color={ButtonColor.Primary}
              icon={<SaveIcon />}
              title={i18next.t('save_button')}
              disabled={!configName || configName.trim() === ''}
              onClick={onSave}
              className={styles.control}
              testId="patient-page-save-button"
            />
            <UniLink
              color={ButtonColor.Primary}
              icon={<CloseIcon />}
              title={i18next.t('close_button')}
              onClick={() => onCloseEditMode(true)}
              testId="patient-page-close-button"
              className={styles.control}
            />
          </div>
        </div>

        <div className={styles.dataContainer}>
          <div
            ref={blocksContainer}
            className={styles.dataBlocks}
            data-test-id="patient-page-overview"
            data-is-edit={isEditMode}
          >
            <ReactGridLayout
              layout={layout}
              droppingItem={
                currentDropElement && blocks[currentDropElement]
                  ? {
                      i: currentDropElement,
                      w: blocks[currentDropElement].w,
                      h: blocks[currentDropElement].h,
                    }
                  : undefined
              }
              {...defaultProps}
            >
              {layout.map((item) => {
                return (
                  <div key={item.i}>
                    {(!configurationQuery.data?.isDefault ||
                      !blocks[item.i]?.static) &&
                      isEditMode && (
                        <IconButton
                          color="grey"
                          iconComponent={<SmallCloseIcon />}
                          className={styles.closeButton}
                          onClick={() => onRemoveBlock(item.i)}
                          testId="block-close-icon"
                        />
                      )}
                    {blocks[item.i]?.component}
                  </div>
                );
              })}
            </ReactGridLayout>
          </div>
          <div
            className={styles.toolbox}
            data-visible={isEditMode}
            data-test-id="patient-page-blocks-section"
          >
            <div className={styles.toolboxTitle}>
              {i18next.t('blocks_title')}
            </div>
            <div
              className={styles.dragBlocks}
              data-test-id="patient-page-patient-blocks"
            >
              {addingKeys
                .filter((key) => !blocks[key].isHidden)
                .map((key) => {
                  return (
                    <div
                      data-grid={{ w: 3, h: 3 }}
                      className={styles.dragBlock}
                      draggable={true}
                      unselectable="on"
                      key={key}
                      onDragStart={() => setCurrentDropElement(key)}
                      data-test-id="patient-page-patient-block-item"
                    >
                      <PointsIcon className={styles.pointsIcon} />
                      <div
                        className={styles.dragBlockIcon}
                        data-test-id="patient-page-drag-block-icon"
                      >
                        <EditorEmptyBlockIcon />
                      </div>
                      <span className={styles.dragBlockTitle}>
                        {defaultBlockNames.includes(key)
                          ? i18next.t(`blocks.${snakeCase(key)}` as any)
                          : dataBlockTypesDict[blocks[key].id].value}
                      </span>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </Loading>
    </div>
  );
};
